import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search } from 'lucide-react';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

const NotFoundPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname || '/';
  const [resolving, setResolving] = useState(true);

  // 1) Check active redirects first — if this URL is in the map, navigate there
  //    silently instead of showing the 404. Then 2) track the hit only if no
  //    redirect was applied.
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const r = await api.get('/url-redirects').catch(() => ({ data: [] }));
        const redirects = Array.isArray(r.data) ? r.data : [];
        const match = redirects.find(x => x.from_path === path);
        if (match && mounted) {
          // Soft redirect — use replace so back button skips the 404 hop.
          navigate(match.to_path || '/', { replace: true });
          return;
        }
      } catch (_) { /* ignore */ }

      if (!mounted) return;
      setResolving(false);
      // Fire-and-forget tracking
      api.post('/url-404/track', {
        path,
        referrer: document.referrer || ''
      }).catch(() => {});
    };
    run();
    return () => { mounted = false; };
  }, [path, navigate]);

  if (resolving) {
    // Tiny grace period so we don't flash 404 while redirect is being applied
    return <div className="min-h-screen bg-[#0A0A0A]" data-testid="not-found-resolving" />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden flex items-center justify-center px-6">
      {/* Decorative grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
        <svg width="100%" height="100%">
          <pattern id="nf-grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#D4AF37" strokeWidth="0.5"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#nf-grid)"/>
        </svg>
      </div>

      {/* Spotlight */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '50%',
          left: '50%',
          width: 900,
          height: 900,
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(212,175,55,0.10) 0%, transparent 60%)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-2xl w-full text-center"
        data-testid="not-found-page"
      >
        <Link to="/" className="inline-block mb-8">
          <img src={LOGO_URL} alt="Bravo" className="h-12 mx-auto opacity-90" />
        </Link>

        {/* Glitch-style 404 */}
        <div className="relative inline-block mb-6">
          <h1
            className="text-[120px] sm:text-[180px] md:text-[220px] leading-none font-black tracking-tighter"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF 0%, #D4AF37 60%, #B8862E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 60px rgba(212,175,55,0.15)',
            }}
          >
            404
          </h1>
          <motion.div
            className="absolute inset-0 text-[120px] sm:text-[180px] md:text-[220px] leading-none font-black tracking-tighter text-[#D4AF37]/10 pointer-events-none"
            animate={{ x: [-2, 2, -2, 0], y: [0, 1, -1, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
            aria-hidden
          >
            404
          </motion.div>
        </div>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-5">
          <Search size={12} className="text-[#D4AF37]" />
          <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-mono">Página no encontrada</span>
        </div>

        <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-white mb-3">
          Esta ruta no existe
        </h2>
        <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto mb-2">
          La URL{' '}
          <code className="bg-white/10 border border-white/15 px-1.5 py-0.5 rounded text-[#D4AF37] font-mono text-xs">
            {path.length > 60 ? `${path.slice(0, 57)}…` : path}
          </code>{' '}
          no corresponde a ninguna sección del sitio.
        </p>
        <p className="text-white/40 text-xs mb-10">
          Puede ser un enlace viejo, un error de tipeo o un evento que ya no está disponible.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate(-1)}
            data-testid="not-found-back-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold uppercase tracking-wider text-xs transition-all min-w-[180px]"
          >
            <ArrowLeft size={16} />
            Volver atrás
          </button>
          <Link
            to="/"
            data-testid="not-found-home-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#B8862E] text-black font-bold uppercase tracking-wider text-xs transition-all min-w-[180px]"
          >
            <Home size={16} />
            Ir al inicio
          </Link>
        </div>

        {/* Quick links */}
        <div className="mt-12 pt-8 border-t border-white/5 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl mx-auto">
          {[
            { to: '/eventos', label: 'Eventos' },
            { to: '/productoras', label: 'Productoras' },
            { to: '/djs', label: 'DJs' },
            { to: '/mi-cuenta', label: 'Mi Cuenta' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-[11px] uppercase tracking-wider text-white/50 hover:text-[#D4AF37] transition-colors py-2"
              data-testid={`not-found-quicklink-${l.to.slice(1)}`}
            >
              {l.label} →
            </Link>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default NotFoundPage;
